import React, { useState, useEffect } from 'react';
import { FaCookieBite, FaTimes, FaChevronDown, FaChevronUp, FaShieldAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import './CookieConsent.css';

const COOKIE_KEY = 'cg_cookie_consent';

const defaultPrefs = {
  necessary: true,      // always true, can't be toggled
  analytics: false,
  marketing: false,
  preferences: false,
};

const CookieConsent = ({ authOpen = false }) => {
  const { isLoggedIn, updateProfile } = useAuth();
  const [visible, setVisible] = useState(false);
  const [pendingShow, setPendingShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);
  // Ref so the mount-only useEffect can read authOpen without needing it as a dep
  const authOpenRef = React.useRef(authOpen);
  authOpenRef.current = authOpen;

  // On mount — runs once. Uses ref so authOpen isn't a required dep.
  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      const t = setTimeout(() => {
        if (authOpenRef.current) {
          setPendingShow(true);
        } else {
          setVisible(true);
        }
      }, 2000);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When auth modal closes and we have a pending show, display the banner
  useEffect(() => {
    if (!authOpen && pendingShow) {
      const t = setTimeout(() => {
        setVisible(true);
        setPendingShow(false);
      }, 800);
      return () => clearTimeout(t);
    }
    if (authOpen && visible) {
      setVisible(false);
      setPendingShow(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOpen]); // only re-run when authOpen changes

  const saveConsent = async (chosenPrefs) => {
    const payload = { ...chosenPrefs, necessary: true, respondedAt: new Date().toISOString() };
    localStorage.setItem(COOKIE_KEY, JSON.stringify(payload));
    setVisible(false);
    // If logged in, persist to their profile
    if (isLoggedIn) {
      try {
        await updateProfile({ cookieConsent: payload });
      } catch {}
    }
  };

  const acceptAll = () => saveConsent({ analytics: true, marketing: true, preferences: true });
  const rejectAll = () => saveConsent({ analytics: false, marketing: false, preferences: false });
  const saveSelected = () => saveConsent(prefs);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  if (!visible) return null;

  return (
    <div className={`cookie-banner ${showDetails ? 'expanded' : ''}`} id="cookie-consent-banner" role="dialog" aria-label="Cookie consent">
      <div className="cookie-inner">
        {/* Top row */}
        <div className="cookie-top">
          <div className="cookie-icon-wrap">
            <FaCookieBite className="cookie-icon" />
          </div>
          <div className="cookie-text">
            <h3 className="cookie-title">We use cookies</h3>
            <p className="cookie-desc">
              We use cookies to improve your experience, remember your preferences, and analyse site traffic.
              You can choose which cookies to allow. See our{' '}
              <a href="#privacy" className="cookie-link">Privacy Policy</a>.
            </p>
          </div>
          <button className="cookie-dismiss-btn" onClick={rejectAll} aria-label="Close" id="cookie-dismiss-btn">
            <FaTimes />
          </button>
        </div>

        {/* Action buttons */}
        <div className="cookie-actions">
          <button className="btn-primary cookie-accept-all" onClick={acceptAll} id="cookie-accept-all">
            <FaShieldAlt />
            <span>Accept All</span>
          </button>
          <button className="btn-outline cookie-reject" onClick={rejectAll} id="cookie-reject-all">
            Reject Non-Essential
          </button>
          <button
            className="cookie-manage-btn"
            onClick={() => setShowDetails(!showDetails)}
            id="cookie-manage-btn"
          >
            Manage Preferences
            {showDetails ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>

        {/* Expanded preferences */}
        {showDetails && (
          <div className="cookie-prefs">
            <div className="cookie-pref-row">
              <div className="cookie-pref-info">
                <span className="cookie-pref-name">Strictly Necessary</span>
                <span className="cookie-pref-desc">Required for the site to function (cart, session, auth). Cannot be disabled.</span>
              </div>
              <div className="cookie-toggle always-on">Always On</div>
            </div>

            {[
              { key: 'preferences', label: 'Preferences', desc: 'Remembers your saved address, language, and settings.' },
              { key: 'analytics', label: 'Analytics', desc: 'Helps us understand how visitors use the site (e.g. Google Analytics). No personal data sold.' },
              { key: 'marketing', label: 'Marketing', desc: 'Used to show relevant ads and track campaign performance.' },
            ].map(({ key, label, desc }) => (
              <div className="cookie-pref-row" key={key}>
                <div className="cookie-pref-info">
                  <span className="cookie-pref-name">{label}</span>
                  <span className="cookie-pref-desc">{desc}</span>
                </div>
                <button
                  className={`cookie-toggle ${prefs[key] ? 'on' : 'off'}`}
                  onClick={() => toggle(key)}
                  aria-pressed={prefs[key]}
                  id={`cookie-toggle-${key}`}
                >
                  <span className="toggle-knob" />
                </button>
              </div>
            ))}

            <button className="btn-primary cookie-save-prefs" onClick={saveSelected} id="cookie-save-prefs">
              Save My Preferences
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
